import React, { useState } from 'react';
import './Book.css';
import { DayPickerRangeController, FocusedInputShape, isInclusivelyAfterDay } from 'react-dates';
import moment from 'moment';
import { TransitionGroup, CSSTransition } from 'react-transition-group';
// import { Redirect } from 'react-router';
import Spinner from './Spinner';
import {
    SessionTypes,
    BookingSkills,
    Question,
    WhenQuestion,
    ActivitiesQuestion,
    GroupSizeQuestion,
    WhoQuestion,
    WhoIsDoingQuestion,
    SkillsQuestion,
    ActivityTypeQuestion,
    ConfirmQuestion,
    SentQuestion,
    WidgetBookingRequestV1,
    WidgetBookingResponseV1
} from '../OutdrTypes';
import _ from 'lodash';

import RobotIcon from './icons/RobotIcon';
// import { gaEvent } from '../analytics';
const gaEvent = (x: any) => { };
import { ReactNode, Fragment } from 'react';
import { useScrollToRefOnMount } from '../hooks/ScrollToOnMount';
import { WidgetAction } from '../actions/WidgetActions';
import { t, existsT } from '../i18n';
import { ButtonPosition } from './Widget';

interface DispatchProps {
    dispatch: (action: WidgetAction) => void;
}

const QuestionComponent = ({ question }: { question: Question }) => {
    switch (question.type) {
        case 'when':
            if (question.activities.length === 1) {
                return <BotMessage>{t('widget.question.when-activity', {activity: question.activities[0]})}</BotMessage>
            }
            return <BotMessage>{t('widget.question.when-location', {location: question.location})}</BotMessage>;
        case 'activities':
            if (_.keys(question.days).length === 1) {
                return <BotMessage>{t('widget.question.activities-days', {activity: _.keys(question.days)[0]})}</BotMessage>
            }
            return <BotMessage>{t('widget.question.activities-which')}</BotMessage>;
        case 'group-size':
            return <BotMessage>{t('widget.question.group-size')}</BotMessage>;
        case 'who':
            return <BotMessage>{t('widget.question.who', {count: question.groupSize})}</BotMessage>;
        case 'who-is-doing':
            return <BotMessage>{t('widget.question.who-is-doing', {activity: question.activity})}?</BotMessage>;
        case 'skill':
            return <BotMessage>{t('widget.question.skill', {activity: question.activity, count: question.groupSize, name: question.name})}</BotMessage>;
        case 'activity-type':
            return <BotMessage>{t('widget.question.activity_type')}</BotMessage>;
        case 'confirm':
            return <SendRequestQuestionComponent {...question} />;
        case 'sent':
            return <BotMessage>{t('widget.question.sending')}</BotMessage>;
    }
    console.error("couldn't find question component for:", question);
    return null;
}

const AnswerComponent = ({ question, dispatch }: { question: Question } & DispatchProps) => {
    switch (question.type) {
        case 'when':
            return <WhenAnswerComponent {...question} dispatch={dispatch} />;
        case 'activities':
            return <ActivitiesAnswerComponent {...question} dispatch={dispatch} />;
        case 'group-size':
            return <GroupSizeAnswerComponent {...question} dispatch={dispatch} />;
        case 'who':
            return <WhoAnswerComponent {...question} dispatch={dispatch} />;
        case 'who-is-doing':
            return <WhoIsDoingAnswerComponent {...question} dispatch={dispatch} />;
        case 'skill':
            return <SkillsAnswerComponent {...question} dispatch={dispatch} />;
        case 'activity-type':
            return <ActivityTypeAnswerComponent {...question} dispatch={dispatch} />;
        case 'confirm':
            return <SendRequestAnswerComponent {...question} dispatch={dispatch} />;
        case 'sent':
            return <SentAnswerComponent {...question} dispatch={dispatch} />;
    }
    console.error("couldn't find answer component for:", question);
    return null;
};

export const BookingNowButton = ({ dispatch, position }: DispatchProps & {position: ButtonPosition}) => (
    <div className={`bookNowButton ${position} flex-row`} onClick={() => dispatch({ type: 'book-now-clicked' })}>
        <RobotIcon />
        {t('widget.book-now')}
    </div>
);

export const BubbleTransition = ({ children, key }: { children: React.ReactNode, key?: React.Key }) => (
    <CSSTransition
        key={key}
        classNames="bubbleTransition"
        timeout={{ enter: 500, exit: 300 }}
    >
        <div>
            {children}
        </div>
    </CSSTransition>
);

export const BookingConversationComponent = ({ questions, sent, dispatch }: { questions: Question[], sent: boolean } & DispatchProps) => (
    <div className="bookChatContainer">
        <ChatHeader dispatch={dispatch} />
        <div className={"bookChat" + (sent ? " sent" : "")}>
            <TransitionGroup>
                <BotMessage>{t('widget.welcome')}</BotMessage>
                {questions.map((q, i) =>
                    (q.show && !q.skip)
                        ? <BubbleTransition key={i}>
                            <QuestionComponent question={q} />
                            <AnswerComponent question={q} dispatch={dispatch} />
                        </BubbleTransition>
                        : <Fragment key={i} />
                )}
            </TransitionGroup>
        </div>
        <div className="poweredBy">{t('widget.powered-by')} <i>outdr.com</i></div>
    </div>
);

const ChatHeader = ({ dispatch }: DispatchProps) => (
    <div className="bookChatHeader">
        <div className="flex-row flex-center">
            <span className="bookChatAvatar"><RobotIcon /></span>
            <b>{t('widget.header-text')}</b>
        </div>
        <button className="btn btn-small" onClick={() => dispatch({ type: 'close-clicked' })}>&times;</button>
    </div>
);

const ActivitiesAnswerComponent = (props: ActivitiesQuestion & DispatchProps) => {
    const { nextDisabled, editing, dispatch, days, maxDays, qn } = props;

    const singleActivity = _.keys(days).length === 1;

    if (!editing) {
        return (
            <CustomerMessage onEdit={() => dispatch({ type: 'edit', qn })}>
                <div className="flex-col">
                    {
                        _.filter(_.toPairs(days), ([_, d]) => d > 0)
                            .map(([activity, d], i) =>
                                singleActivity 
                                    ? <div key={i}>{t('widget.answer.activities-single-activity-days', {count: d})}</div>
                                    : <div key={i}>{t('widget.answer.activities-activity-days', {count: d, activity})} {activity} ({d} {d === 1 ? 'day' : 'days'})</div>)
                    }
                </div>
            </CustomerMessage>
        );
    }

    return (
        <CustomerMessageEdit>
            <div className="flex-col flex-center">
                {
                    _.keys(days).map(a => {
                        const activity = a;
                        const d = days[activity];
                        const isSelected = d > 0;
                        return (
                            <div key={a} className="flex-row flex-center">
                                {
                                    !singleActivity &&
                                    <div
                                        style={{ width: '150px' }}
                                        className={`bookSelField ${isSelected ? 'bookSelField-selected' : ''}`}
                                        onClick={() => dispatch({ type: 'activity-clicked', activity })}
                                    >
                                        {a}
                                    </div>
                                }
                                <div
                                    className="flex-row flex-center"
                                    style={{ padding: '0px 5px 0px 5px' }}
                                >
                                    <button
                                        disabled={d <= 0}
                                        className="btn btn-vsmall"
                                        onClick={() => dispatch({ type: 'activity-days-changed', activity, delta: -1 })}>
                                        {'-'}
                                    </button>
                                    <div style={{ width: '60px' }}>{d} {d === 1 ? 'day' : 'days'}</div>
                                    <button
                                        disabled={d >= maxDays}
                                        className="btn btn-vsmall"
                                        onClick={() => dispatch({ type: 'activity-days-changed', activity, delta: +1 })}>
                                        {'+'}
                                    </button>
                                </div>
                            </div>
                        );
                    })
                }
                <SendMessageButton disabled={nextDisabled} onClick={() => dispatch({ type: 'activity-next' })} />
            </div>
        </CustomerMessageEdit>
    );
};

const WhoIsDoingAnswerComponent = (props: WhoIsDoingQuestion & DispatchProps) => {
    const { activity, nextDisabled, editing, groupSize, groupNames, selected, dispatch, qn } = props;

    if (!editing) {
        return (
            <CustomerMessage onEdit={() => dispatch({ type: 'edit', qn })}>
                {
                    groupNames
                        .filter((_, i) => selected[i] && i < groupSize)
                        .join(', ')
                }
            </CustomerMessage>
        );
    }

    return (
        <CustomerMessageEdit>
            <div className="flex-col flex-center">
                {
                    groupNames.slice(0, groupSize).map((m, i) => {
                        const isSelected = selected[i];
                        return (
                            <div key={i} className="flex-row flex-center">
                                <div
                                    style={{ width: '150px' }}
                                    className={`bookSelField ${isSelected ? 'bookSelField-selected' : ''}`}
                                    onClick={() => dispatch({ type: 'who-activity-toggle', index: i, activity })}
                                >
                                    {m || t('widget.answer.who-is-doing-guest', {number: i})}
                                </div>
                            </div>
                        );
                    })
                }
                <SendMessageButton disabled={nextDisabled} onClick={() => dispatch({ type: 'who-activity-next', activity })} />
            </div>
        </CustomerMessageEdit>
    );
};

const SkillsAnswerComponent = (props: SkillsQuestion & DispatchProps) => {
    const { activity, index, groupSize, dispatch, skill, editing, name, qn } = props;
    const single = groupSize === 1;

    const translateSkillLevel = (s: string) => {
        const key = `widget.skills.${s}`;
        const translated = t(key);
        return translated === key ? s : translated;
    };

    const getSkillStatement = (s: string): { skillStatement: string, estimated: boolean } => {
        const key = `widget.skill-statements.${activity}.${s}`;

        if (existsT(key)) {
            return {
                skillStatement: t(key),
                estimated: true
            };
        }

        return {
            skillStatement: translateSkillLevel(s),
            estimated: false
        }
    };

    const { skillStatement, estimated } = getSkillStatement(skill)
    const translatedSkill = translateSkillLevel(skill);
    return (
        !editing
            ? <>
                <CustomerMessage onEdit={() => dispatch({ type: 'edit', qn })}>
                    {skillStatement}
                </CustomerMessage>

                {estimated &&
                    <BotMessage>
                        {
                            single
                                ? t('widget.answer.skills-estimate-single', {activity, level: translatedSkill})
                                : t('widget.answer.skills-estimate', {activity, level: translatedSkill, name})
                        }
                    </BotMessage>
                }
            </>
            : <CustomerMessageEdit>
                <div className="flex-row flex-wrap">
                    {
                        BookingSkills.map((s) =>
                            <div
                                key={s}
                                className={`bookSelField${s === skill ? ' bookSelField-selected' : ''}`}
                                onClick={() => dispatch({ type: 'skills-select', index, skill: s, activity })}
                            >
                                {getSkillStatement(s).skillStatement}
                            </div>
                        )
                    }
                </div>
            </CustomerMessageEdit>
    );
};

const ActivityTypeAnswerComponent = (props: ActivityTypeQuestion & DispatchProps) => {
    const { activity, dispatch, editing, activityType, qn } = props;

    return (
        !editing ?
            <CustomerMessage onEdit={() => dispatch({ type: 'edit', qn })}>
                {activity} {t(`widget.session-types.${activityType}`)}
            </CustomerMessage> :
            <CustomerMessageEdit>
                <div className="flex-col">
                    {
                        SessionTypes.map((st) =>
                            <div
                                key={st.name}
                                className={`bookSelField ${st.name === activityType ? 'bookSelField-selected' : ''}`}
                                onClick={() => dispatch({ type: 'type-activity-select', activity, activityType: st.name })}
                            >
                                {activity} {t(`widget.session-types.${st.name}`)}
                            </div>
                        )
                    }
                </div>
            </CustomerMessageEdit>
    );
};

const GroupSizeAnswerComponent = (props: GroupSizeQuestion & DispatchProps) => {
    const { editing, groupSize, dispatch, qn, maxGroupSize } = props;

    if (!editing) {
        return (
            <CustomerMessage onEdit={() => dispatch({ type: 'edit', qn })}>
                {t('widget.answer.group-size-people', {count: groupSize})}
            </CustomerMessage>
        );
    }

    return (
        <CustomerMessageEdit>
            <div className="flex-row flex-wrap">
                {
                    _.range(1, maxGroupSize + 1).map(i =>
                        <div
                            key={i}
                            className="bookSelField"
                            onClick={() => dispatch({ type: 'groupsize-select', size: i })}
                        >
                            {t('widget.answer.group-size-people', {count: i})}
                        </div>)
                }
            </div>
        </CustomerMessageEdit>
    );
};

const WhoAnswerComponent = (props: WhoQuestion & DispatchProps) => {
    const { group, groupSize, dispatch, editing, nextDisabled, qn } = props;

    if (!editing) {
        return (
            <CustomerMessage onEdit={() => dispatch({ type: 'edit', qn })}>
                <div className="flex-col">
                    {
                        group.map((m, i) =>
                            i < groupSize
                            && m.name
                            && <div key={i} style={{ padding: '5px' }}>
                                {m.name}{m.email ? `, ${m.email}` : ''}
                            </div>
                        )
                    }
                </div>
            </CustomerMessage>
        );
    }

    return (
        <>
            <CustomerMessageEdit>
                <div className="flex-col flex-center">
                    {
                        group.map((m, i) =>
                            i < groupSize
                            && <div className="bookNameEmail" key={i}>
                                { i === 1 && <p style={{fontSize: '60%'}}>{t('widget.answer.who-optional')}</p>}
                                <input
                                    type="text"
                                    name="name"
                                    value={m.name}
                                    disabled={!m.editable}
                                    placeholder={i > 0 ? t('widget.answer.who-name-placeholder-guest', {number: i+1}) : t('widget.answer.who-name-placeholder')}
                                    onChange={ev => dispatch({ type: 'who-update-name', index: i, name: ev.target.value })}
                                    onFocus={ev => ev.target.select()}
                                    autoComplete={i === 0 ? 'name' : 'off'}
                                    required={i === 0}
                                />
                                <input
                                    type="email"
                                    name="email"
                                    value={m.email}
                                    disabled={!m.editable}
                                    placeholder={i > 0 ? t('widget.answer.who-email-placeholder-guest', {number: i+1}) : t('widget.answer.who-email-placeholder')}
                                    onChange={ev => dispatch({ type: 'who-update-email', index: i, email: ev.target.value })}
                                    onFocus={ev => ev.target.select()}
                                    autoComplete={i === 0 ? 'email' : 'off'}
                                    required={i === 0}
                                    
                                />
                                {
                                    m.requestTel &&
                                    <input
                                        type="tel"
                                        name="tel"
                                        value={m.tel}
                                        disabled={!m.editable}
                                        placeholder={i > 0 ? t('widget.answer.who-tel-placeholder-guest', {number: i+1}) : t('widget.answer.who-tel-placeholder')}
                                        onChange={ev => dispatch({ type: 'who-update-tel', index: i, tel: ev.target.value })}
                                        onFocus={ev => ev.target.select()}
                                        autoComplete={i === 0 ? 'tel' : 'off'}
                                        required={false}
                                    />
                                }
                            </div>
                        )
                    }
                    <SendMessageButton disabled={nextDisabled} onClick={() => dispatch({ type: 'who-next' })} />
                </div>
            </CustomerMessageEdit>
            {group.length > 1 && <span className="bookChatInfo">{t('widget.answer.who-footer')}</span>}
        </>
    );
};

const SendMessageButton = (props: { disabled?: boolean, onClick?: () => void }) => {
    return (
        <div className="bookChatButtonRow">
            <button className="btn btn-small btn-small-highlight" {...props}>></button>
        </div>
    );
};

const SendRequestQuestionComponent = ({ request }: ConfirmQuestion) => {
    if (!request) {
        throw new Error("assertion error: question.request should not be null");
    }
    const { start, end, email, activities } = request;

    return (
        <>
            <BotMessage>
                <p>{t('widget.question.send-header')}</p>
                {start && end && start === end && <div>{start.format('ddd DD/MMM')}</div>}
                {start && end && start !== end && <div>{t('widget.question.send-date-range', {start: start.format('ddd DD/MMM'), end: end.format('ddd DD/MMM'), interpolation: { escapeValue: false }})}</div>}
                {
                    _.map(activities, ({ activity, days, sessionType: type }) => (
                        <div key={activity}>
                            {t('widget.question.send-days-activity-type', {days, activity, type, count: days})}
                        </div>
                    ))
                }
                {email && <p>{email}</p>}
            </BotMessage>
            <BotMessage>{t('widget.question.send-question')}</BotMessage>
        </>
    );
}

const send = async (dispatch: (action: WidgetAction) => void, request: WidgetBookingRequestV1, postUrl: string) => {
    dispatch({ type: 'send' });

    try {
        const response = await fetch(
            postUrl,
            {
                body: JSON.stringify(request),
                method: 'PUT',
                headers: [['content-type', 'application/json']]
            }
        );
        const json = await response.text();
        dispatch({ type: 'response', json })
    } catch (err) {
        const error: WidgetBookingResponseV1 = { code: 'ERROR', message: err.message };
        dispatch({ type: 'response', json: JSON.stringify(error) })
    }
};

const fakeSend = async (dispatch: (action: WidgetAction) => void, request: WidgetBookingRequestV1, postUrl: string) => {
    dispatch({ type: 'send' });

    try {
        console.info(`send request to ${postUrl}`);
        console.info(JSON.stringify(request));
        await new Promise((resolve) => setTimeout(resolve, 2000));
        const success: WidgetBookingResponseV1 = { code: 'SUCCESS', bookingId: '1234', viewUrl: 'http://google.com' };
        dispatch({ type: 'response', json: JSON.stringify(success) })
    } catch (err) {
        const error: WidgetBookingResponseV1 = { code: 'ERROR', message: err.message };
        dispatch({ type: 'response', json: JSON.stringify(error) })
    }
};


const SendRequestAnswerComponent = (props: ConfirmQuestion & DispatchProps) => {
    const ref = useScrollToRefOnMount<HTMLDivElement>();

    const { editing, dispatch, request, apiDomain, fakeApi } = props;

    if (!request) {
        throw new Error("assertion error: question.request should not be null");
    }

    if (!editing) {
        return (
            <CustomerMessage>{t('widget.answer.send-yes')}</CustomerMessage>
        );
    }

    const url = `https://${apiDomain}/api/public/widget/createBooking/v1`;

    const doSend = fakeApi ? fakeSend : send;

    return (
        <div className="flex-col flex-center" ref={ref}>
            <div className="bookChatButtonRow">
                <button
                    className="btn btn-primary"
                    onClick={() => doSend(dispatch, request, url)}
                >
                    {t('widget.answer.send-yes')}
                </button>
                <button
                    className="btn btn-secondary"
                    onClick={() => dispatch({ type: 'reset' })}
                >
                    {t('widget.answer.send-no')}
                </button>
            </div>
            <span className="bookChatInfo">
                {t('widget.answer.send-footer')}
            </span>
        </div>
    );
};

const SentAnswerComponent = ({ response, dispatch }: SentQuestion & DispatchProps) => {
    if (!response) {
        return <BotMessage><Spinner /></BotMessage>;
    }
    switch (response.code) {
        case 'SUCCESS':
            return (
                <>
                    <BotMessage>{t('widget.answer.sent-success-message')}</BotMessage>
                    <div className="bookChatButtonRow">
                        <button className="btn btn-primary" onClick={() => dispatch({ type: 'view-booking' })}>{t('widget.answer.sent-view-button-text')}</button>
                        <button className="btn btn-secondary" onClick={() => dispatch({ type: 'reset' })}>{t('widget.answer.sent-close-button-text')}</button>
                    </div>
                </>
            );
        case 'ERROR':
            return <p>{t('widget.answer.sent-error-message')}</p>
        default:
            return <p>Huh?! {JSON.stringify(response)}</p>
    }
};

const WhenAnswerComponent = (props: WhenQuestion & DispatchProps) => {
    const [focusedInput, setFocusedInput] = useState(null as FocusedInputShape | null);
    const { start, end, editing, nextDisabled, nDays, qn, dispatch } = props;

    const datesChange = (dates: { startDate: moment.Moment | null, endDate: moment.Moment | null }) => {
        dispatch({ type: 'when-set-dates', ...dates });
    };

    if (!editing && start && end) {
        return (
            <CustomerMessage onEdit={() => dispatch({ type: 'edit', qn })}>
                {
                    start.isSame(end)
                        ? `${moment(start).format('ddd DD/MMM')}`
                        : t('widget.answer.when-selected-date-range', {start: moment(start).format('ddd DD/MMM'), end: moment(end).format('ddd DD/MMM'), days: nDays, interpolation: { escapeValue: false }})
                }
            </CustomerMessage>
        );
    }
    return (
        <div className="flex-col flex-center">
            <DayPickerRangeController
                startDate={start}
                endDate={end}
                minimumNights={0}
                numberOfMonths={1}
                noBorder={true}
                orientation="horizontal"
                onDatesChange={datesChange}
                focusedInput={focusedInput || 'startDate'}
                onFocusChange={setFocusedInput}
                isOutsideRange={day => !isInclusivelyAfterDay(day, moment())}
            />
            <SendMessageButton disabled={nextDisabled} onClick={() => dispatch({ type: 'when-next' })} />
        </div>
    );
};

const BotMessage = ({ children }: { children: ReactNode }) => {
    const ref = useScrollToRefOnMount<HTMLDivElement>();

    return (
        <div className="botMessageBubble" ref={ref}>
            {children}
        </div>
    );
};

const CustomerMessage = ({ children, onEdit }: { children: ReactNode, onEdit?: () => void }) => {
    const ref = useScrollToRefOnMount<HTMLDivElement>();

    return (
        <div className="customerMessageBubble" ref={ref} onClick={onEdit}>
            <span className="customerMessageBubbleEditIcon" />
            {children}
        </div>
    );
};

const CustomerMessageEdit = ({ children }: { children: ReactNode }) => {
    const ref = useScrollToRefOnMount<HTMLDivElement>();

    return (
        <div className="customerMessageBubbleEdit" ref={ref}>
            {children}
        </div>
    );
};
